import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import debounce from 'lodash/debounce';
import Slider from 'rc-slider';

import { FormGroup, Label } from 'ui-library';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '../../locale';
import { getClassNames } from '../../utils';
import { unformatCurrency } from '../../utils/formatting';

import Error from '../AtomicStructure/atoms/Error';
import './rc-slider.css';
import './RangeSlider.css';

type RangeSliderProps = {
    suffix?: string;
    htmlFor?: string;
    label: string;
    labelInfobox?: boolean;
    value?: string | number;
    onChange?: (val?: number) => void;
    error?: string;
    min?: number;
    max?: number;
    disabled?: boolean;
    unit?: 'years' | 'currency';
    showMinMax?: boolean;
    withInput?: boolean;
    enableZero?: boolean;
    disableExceedingMaxValue?: boolean;
};

const RangeSlider = ({
    label,
    withInput,
    enableZero,
    suffix = '',
    htmlFor = undefined,
    error = undefined,
    labelInfobox = true,
    value = 0,
    onChange = () => {},
    min = 0,
    max = 100,
    disabled = false,
    unit = 'currency',
    showMinMax = false,
    disableExceedingMaxValue,
}: RangeSliderProps) => {
    const [val, setVal] = useState<number | undefined>(+value);
    const { getFormattedCurrency } = useFormatting();
    const {
        t,
        i18n: { language },
    } = useTranslation();

    useEffect(() => {
        setVal(+value);
    }, [value]);

    const debouncedSetMinValue = useCallback(
        debounce((sliderValue) => {
            if (
                (sliderValue < min && enableZero && sliderValue !== 0)
                || (!enableZero && sliderValue < min)
            ) {
                setVal(min);
                onChange(min);
            }
        }, 1000),
        [min, enableZero],
    );

    const onChangeSlider = useCallback(
        (sliderValue) => {
            const newVal = disableExceedingMaxValue && (sliderValue > max) ? max : sliderValue;

            setVal(newVal);
            debouncedSetMinValue(newVal);
        },
        [min, max, disableExceedingMaxValue],
    );

    const handleChangeComplete = useCallback(
        (sliderValue) => {
            const newVal = disableExceedingMaxValue && (sliderValue > max) ? max : sliderValue;

            onChange(newVal);
        },
        [max, disableExceedingMaxValue],
    );

    const handleBetterChangeSlider = useCallback(
        debounce((myVal) => {
            handleChangeComplete(myVal);
        }, 1000),
        [handleChangeComplete],
    );

    const getYearText = (yearVal) => {
        switch (yearVal) {
            case 0:
                return t('RangeSlider.Unit.Year.Zero');

            case 1:
                return t('RangeSlider.Unit.Year');

            default:
                return t('RangeSlider.Unit.Years');
        }
    };

    const displayValue = useMemo(() => {
        if (unit === 'years') {
            return `${val} ${getYearText(val)}`;
        }

        return getFormattedCurrency(val, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }, [val, unit, language, getYearText]);

    return (
        <FormGroup>
            <Label
                htmlFor={htmlFor}
                label={label}
                labelInfobox={labelInfobox}
            />

            <div className="slider-wrapper">
                {!withInput ? (
                    <div className="slider-value">{displayValue}</div>
                ) : (
                    <div className="slider-value">
                        <span
                            className={getClassNames('slider-input-wrapper', {
                                'is-disabled': disabled,
                            })}
                        >
                            {unit === 'currency' && (
                                <span className="slider-currency">CHF</span>
                            )}
                            <input
                                disabled={disabled}
                                className="TextInput slider-input"
                                value={displayValue}
                                style={{
                                    width: displayValue.length * 11 + 1,
                                }}
                                onChange={(e) => {
                                    const myVal = unformatCurrency(
                                        e.target.value,
                                    );

                                    onChangeSlider(myVal);
                                    if (myVal >= min) {
                                        handleBetterChangeSlider(myVal);
                                    }
                                }}
                            />
                            {suffix}
                        </span>
                    </div>
                )}

                <Slider
                    disabled={disabled}
                    min={min}
                    max={max}
                    onChange={onChangeSlider}
                    value={+(val || 0)}
                    onChangeComplete={handleChangeComplete}
                />
                {showMinMax && (
                    <div className="slider-minmax">
                        <span>
                            CHF
                            {' '}
                            {getFormattedCurrency(min, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            })}
                        </span>
                        <span>
                            CHF
                            {' '}
                            {getFormattedCurrency(max, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                            })}
                        </span>
                    </div>
                )}
            </div>
            {error && <Error>{error}</Error>}
        </FormGroup>
    );
};

export default RangeSlider;
